import { useEffect } from 'react';
import { useAnalyticsTrack, pageInitializedEvent, userDetectedEvent, pageInfoEvent, pageLoadCompletedEvent } from '@chr/react-analytics';
import { getUserDetail, getUserAccessPoliciesAsync } from '@chr/online-shared-microsite-core';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

// since this is just a helper method that allows for mocking,
// no tests are required.
var getWindowLocation = function () {
    return window.location;
};
var getPagePath = function (path) {
    var pagePath = '';
    var location = getWindowLocation();
    if (path) {
        var page = path;
        var colonIndex = path.indexOf(':');
        if (colonIndex >= 0) {
            page = path.slice(0, colonIndex);
        }
        pagePath = "".concat(location.pathname.slice(1), "#").concat(page);
    }
    else {
        pagePath = "".concat(location.host).concat(location.pathname, "#").concat(location.hash);
    }
    return pagePath;
};
var getUserDetails = function (userProfile, isGuest) {
    var _a, _b, _c;
    var userObject = {
        userId: '',
        userType: 'External',
        userEmail: (_a = userProfile === null || userProfile === void 0 ? void 0 : userProfile.email) !== null && _a !== void 0 ? _a : ''
    };
    if (isGuest) {
        userObject.userType = 'Guest';
    }
    else if (userProfile && userProfile.userId) {
        userObject.userId = userProfile.userId.toString();
        var at = (_b = userProfile.email) === null || _b === void 0 ? void 0 : _b.lastIndexOf('@');
        var email = (_c = userProfile.email) === null || _c === void 0 ? void 0 : _c.slice(at);
        if (email == '@chrobinson.com' || email == '@mytmc.com' || email == '@freightquote.com') {
            userObject.userType = 'Internal';
        }
    }
    return userObject;
};
var getUserCustomerCodes = function (accessPolicies) {
    var customerCodes = accessPolicies.filter(function (policy) { var _a; return ((_a = policy.entity) === null || _a === void 0 ? void 0 : _a.entityTypeID) === 2 && !policy.relatedEntity; }).map(function (policy) { var _a, _b; return (_b = (_a = policy === null || policy === void 0 ? void 0 : policy.entity) === null || _a === void 0 ? void 0 : _a.code) !== null && _b !== void 0 ? _b : ''; });
    return customerCodes.length ? customerCodes : null;
};
var getUserSupplier = function (accessPolicies) {
    var _a, _b, _c, _d;
    var supplier = { code: '', name: '' };
    var supplierPolicy = accessPolicies.find(function (policy) { var _a; return ((_a = policy.entity) === null || _a === void 0 ? void 0 : _a.entityTypeID) === 4 && !policy.relatedEntity; });
    if (supplierPolicy) {
        supplier.code = (_b = (_a = supplierPolicy === null || supplierPolicy === void 0 ? void 0 : supplierPolicy.entity) === null || _a === void 0 ? void 0 : _a.code) !== null && _b !== void 0 ? _b : '';
        supplier.name = (_d = (_c = supplierPolicy === null || supplierPolicy === void 0 ? void 0 : supplierPolicy.entity) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '';
        return supplier;
    }
    return null;
};

var useAnalyticsPageView = function (_a) {
    var pageName = _a.pageName, pagePath = _a.pagePath, siteName = _a.siteName, userId = _a.userId, userType = _a.userType, userEmail = _a.userEmail, customerCode = _a.customerCode, customerName = _a.customerName;
    var track = useAnalyticsTrack();
    useEffect(function () {
        var getUserDataAndTrack = function () { return __awaiter(void 0, void 0, void 0, function () {
            var userDetail, customerCodes, supplier, _a, accessPolicies, userDetailResponse, division, branch, userData;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _c.trys.push([0, 3, , 4]);
                        return [4 /*yield*/, Promise.all([getUserAccessPoliciesAsync(), getUserDetail()])];
                    case 1:
                        _a = _c.sent(), accessPolicies = _a[0], userDetailResponse = _a[1];
                        userDetail = userDetailResponse;
                        if (accessPolicies) {
                            customerCodes = getUserCustomerCodes(accessPolicies);
                            supplier = getUserSupplier(accessPolicies);
                        }
                        return [4 /*yield*/, getUserDetail()];
                    case 2:
                        userDetail = _c.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        _c.sent();
                        return [3 /*break*/, 4];
                    case 4:
                        division = userDetail && userDetail.primaryBusinessLine;
                        branch = userDetail && userDetail.primaryOwningBranch;
                        userData = {
                            type: userType,
                            id: userId,
                            division: division !== null && division !== void 0 ? division : 'NotAvailable',
                            branch: branch !== null && branch !== void 0 ? branch : 'NotAvailable'
                        };
                        track(pageInitializedEvent());
                        track(userDetectedEvent(userData));
                        track(pageInfoEvent({
                            name: pageName,
                            path: pagePath,
                        }, {
                            name: siteName,
                        }));
                        track(pageLoadCompletedEvent({
                            page: {
                                // eVar v89
                                name: pageName,
                            },
                            site: {
                                // eVar v88
                                name: siteName
                            },
                            user: {
                                // eVar v100
                                id: userData.id,
                                // eVar v92
                                branch: userData.branch,
                                // eVar v90
                                division: userData.division,
                                // eVar v80
                                type: userType,
                            },
                            customer: {
                                // eVar v94
                                code: customerCode,
                                // eVar v93
                                name: customerName,
                            },
                            infoUi: {
                                // eVar v101
                                user: userEmail,
                            },
                            // eVar v20
                            supplierCode: supplier === null || supplier === void 0 ? void 0 : supplier.code,
                            // eVar v19
                            supplierName: supplier === null || supplier === void 0 ? void 0 : supplier.name,
                            // L3 Customer Code List
                            customerCodeList: customerCodes !== null && customerCodes !== void 0 ? customerCodes : undefined
                        }));
                        return [2 /*return*/];
                }
            });
        }); };
        getUserDataAndTrack();
    }, [
        pageName,
        pagePath,
        siteName,
    ]);
};

export { __rest as _, __assign as a, getPagePath as b, getUserDetails as g, useAnalyticsPageView as u };
//# sourceMappingURL=useAnalyticsPageView-f7488e50.js.map
